import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Printer, Save, Pencil, Sunrise, Moon, Activity, Apple, BedDouble, Wind, Stethoscope, Target, LineChart, Lock, FileDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const SECTIONS = [
  { k: "morning_routine", label: "My morning routine", icon: Sunrise, ph: "How you'd like to start your day" },
  { k: "evening_routine", label: "My evening routine", icon: Moon, ph: "Your wind down for better sleep" },
  { k: "movement_plan", label: "My movement plan", icon: Activity, ph: "Walking and strength that fit your week" },
  { k: "food_foundations", label: "My food foundations", icon: Apple, ph: "Your go to balanced meals" },
  { k: "sleep_support", label: "My sleep supports", icon: BedDouble, ph: "What helps you rest" },
  { k: "stress_tools", label: "My stress tools", icon: Wind, ph: "Your calming go tos" },
  { k: "doctor_questions", label: "My healthcare questions", icon: Stethoscope, ph: "What you'd like to ask" },
  { k: "personal_observations", label: "My personal observations", icon: LineChart, ph: "Patterns you noticed in your own entries" },
];

export default function SupportPlan() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [meta, setMeta] = useState({ days_completed: 0, total_days: 30, program_complete: false });
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/support-plan").then((r) => {
      setPlan(r.data.plan);
      setMeta({
        days_completed: r.data.days_completed ?? 0,
        total_days: r.data.total_days ?? 30,
        program_complete: r.data.program_complete ?? false,
      });
      if (!r.data.exists) setEditing(true);
    });
  }, []);

  const set = (k, v) => setPlan((p) => ({ ...p, [k]: v }));

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/support-plan/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Doctor-Appointment-Report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Your report is downloading");
    } catch (e) {
      toast.error("Could not generate the report. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/support-plan", {
        priorities: plan.priorities || [],
        morning_routine: plan.morning_routine || "",
        evening_routine: plan.evening_routine || "",
        movement_plan: plan.movement_plan || "",
        food_foundations: plan.food_foundations || "",
        sleep_support: plan.sleep_support || "",
        stress_tools: plan.stress_tools || "",
        doctor_questions: plan.doctor_questions || "",
        personal_observations: plan.personal_observations || "",
      });
      toast.success("Your plan is saved");
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!plan) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 rounded-full border-[3px] border-[var(--surface-accent)] border-t-[var(--brand)] animate-spin" /></div>;

  return (
    <div className="px-5 pt-6 animate-fade-rise print-area" data-testid="support-plan-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-ink-muted mb-4 no-print" data-testid="plan-back">
        <ChevronLeft size={20} /> Back
      </button>

      <p className="overline-label">Your personal plan</p>
      <h1 className="font-serif text-3xl font-bold text-ink mt-2">My Menopause Support Plan</h1>
      <p className="text-ink-soft text-sm mt-2">Built from everything you've entered, yours to keep, edit and revisit.</p>

      {!meta.program_complete && (
        <div className="mt-5 bg-sage rounded-2xl p-5 flex items-start gap-3" data-testid="plan-progress-banner">
          <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0">
            <Lock size={16} className="text-brand" />
          </div>
          <div>
            <div className="font-semibold text-ink text-sm">Your full report produces after all 30 days</div>
            <p className="text-ink-soft text-sm mt-1 leading-relaxed">
              Everything you enter each day is saved. This is your plan so far, building from your entries. Your complete Personal Menopause Support Plan is finalised once you complete Day 30 ({meta.days_completed} of {meta.total_days} done).
            </p>
          </div>
        </div>
      )}

      {/* priorities */}
      <div className="mt-6 bg-brand rounded-2xl p-5 text-white">
        <div className="flex items-center gap-2 text-white/70 text-xs font-semibold uppercase tracking-wide">
          <Target size={14} /> My top three priorities
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {(plan.priorities || []).slice(0, 3).map((p) => (
            <span key={p} className="bg-white/15 rounded-full px-3 py-1.5 text-sm font-medium">{p}</span>
          ))}
          {(!plan.priorities || plan.priorities.length === 0) && <span className="text-white/60 text-sm">Set during onboarding</span>}
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.k} className="bg-white rounded-2xl p-5 card-shadow">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-sage flex items-center justify-center"><Icon size={16} className="text-brand" /></div>
                <span className="font-semibold text-ink text-sm">{s.label}</span>
              </div>
              {editing ? (
                <textarea
                  data-testid={`plan-${s.k}`}
                  value={plan[s.k] || ""}
                  onChange={(e) => set(s.k, e.target.value)}
                  rows={3}
                  className="w-full rounded-xl border border-subtle bg-app px-4 py-3 text-ink outline-none focus:border-[var(--brand)] resize-none"
                  placeholder={s.ph}
                />
              ) : (
                <p className="text-ink-soft text-sm leading-relaxed whitespace-pre-wrap">
                  {plan[s.k] ? plan[s.k] : <span className="text-ink-muted italic">Not filled in yet</span>}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex gap-3 mt-6 mb-4 no-print">
        {editing ? (
          <button data-testid="plan-save" onClick={save} disabled={busy} className="flex-1 bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60">
            <Save size={16} /> {busy ? "Saving…" : "Save plan"}
          </button>
        ) : (
          <button data-testid="plan-edit" onClick={() => setEditing(true)} className="flex-1 bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2">
            <Pencil size={16} /> Edit plan
          </button>
        )}
        <button data-testid="plan-download-pdf" onClick={downloadPdf} disabled={downloading} className="flex-1 border-2 border-[var(--brand)] text-brand font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60">
          <FileDown size={16} /> {downloading ? "Preparing…" : "Download PDF"}
        </button>
      </div>

      <button data-testid="plan-print" onClick={() => window.print()} className="w-full text-ink-muted font-medium py-2 rounded-full flex items-center justify-center gap-2 no-print mb-6 text-sm">
        <Printer size={15} /> Or print this page
      </button>

      <p className="text-center text-ink-muted text-xs mb-8 px-4 no-print" data-testid="plan-pdf-note">
        Your PDF is a clean, print-ready Doctor's Appointment Report you can email or hand over at reception.
      </p>
    </div>
  );
}
